// ---------------------------------------------------------------------------
// useSyncStatus — lightweight reactive sync status aggregator (§5.2)
//
// Consumes NamedReplicationStates from setupReplication() and exposes
// a single aggregated status for the Sync Status Widget (§5.3).
// Works across all three apps: tenant-web, system-web, mobile-pwa.
// ---------------------------------------------------------------------------

import type { NamedReplicationStates } from './replicationFactory';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
export type SyncStatus =
  | 'live'          // SSE connected, all collections idle, up to date
  | 'syncing'       // one or more collections actively pulling
  | 'catching-up'   // full pull in progress (first sync or divergence recovery)
  | 'polling'       // SSE disconnected, falling back to divergence check on reconnect
  | 'offline'       // device has no network connectivity
  | 'error'         // one or more collections in error state
  | 'push-failed';  // outbound queue has stuck items (mobile only)

export interface SyncStatusState {
  status: SyncStatus;
  sseStatus: 'live' | 'polling' | 'disconnected';
  activeCollections: string[];
  errorCollections: string[];
  lastSyncAt: string | null;
  totalBehind: number;
  failedPushCount: number;
  isOnline: boolean;
}

// ---------------------------------------------------------------------------
// Subscribers
// ---------------------------------------------------------------------------
type Listener = (state: SyncStatusState) => void;

const listeners = new Set<Listener>();
let currentState: SyncStatusState = {
  status: 'offline',
  sseStatus: 'disconnected',
  activeCollections: [],
  errorCollections: [],
  lastSyncAt: null,
  totalBehind: 0,
  failedPushCount: 0,
  isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
};

function notify(): void {
  for (const fn of listeners) {
    try { fn(currentState); } catch { /* ignore */ }
  }
}

function updateState(partial: Partial<SyncStatusState>): void {
  currentState = { ...currentState, ...partial };
  // Derive top-level status
  currentState.status = deriveStatus(currentState);
  notify();
}

function deriveStatus(s: SyncStatusState): SyncStatus {
  if (!s.isOnline) return 'offline';
  if (s.failedPushCount > 0) return 'push-failed';
  if (s.errorCollections.length > 0) return 'error';
  if (s.activeCollections.length > 0) return 'syncing';
  if (s.sseStatus === 'polling') return 'polling';
  if (s.sseStatus === 'disconnected') return 'offline';
  return 'live';
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** Get current sync status (non-reactive snapshot). */
export function getSyncStatus(): SyncStatusState {
  return currentState;
}

/** Subscribe to sync status changes. Returns unsubscribe function. */
export function subscribeSyncStatus(fn: Listener): () => void {
  listeners.add(fn);
  fn(currentState); // emit current state immediately
  return () => { listeners.delete(fn); };
}

/** Update SSE connection status. Called from replicationFactory or DBProvider. */
export function setSseStatus(status: 'live' | 'polling' | 'disconnected'): void {
  updateState({ sseStatus: status });
}

/** Update online/offline status. */
export function setOnlineStatus(online: boolean): void {
  updateState({ isOnline: online });
}

/** Update failed push count (mobile only). */
export function setFailedPushCount(count: number): void {
  updateState({ failedPushCount: count });
}

/** Wire up NamedReplicationStates to the sync status store. */
export function wireReplicationStates(states: NamedReplicationStates): void {
  for (const [name, state] of states) {
    state.active$.subscribe((active: boolean) => {
      const activeSet = new Set(currentState.activeCollections);
      if (active) activeSet.add(name); else activeSet.delete(name);
      updateState({
        activeCollections: Array.from(activeSet),
        ...(active ? {} : { lastSyncAt: new Date().toISOString() }),
      });
    });

    state.error$.subscribe((err: any) => {
      if (!err) return;
      const errorSet = new Set(currentState.errorCollections);
      errorSet.add(name);
      updateState({ errorCollections: Array.from(errorSet) });
    });
  }
}

// ---------------------------------------------------------------------------
// Auto-track online/offline events
// ---------------------------------------------------------------------------
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => setOnlineStatus(true));
  window.addEventListener('offline', () => setOnlineStatus(false));
}
